import React, { useEffect } from 'react';
import { useField } from 'formik';
import { AssetReference, getProtectedAssetUrl } from '@uplift/api';
import { Box, ClassNameMap, IconButton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { downloadAssetFile, useUploadInsurance } from '@uplift/utils';
import { UpliftThemeProvider } from '../theme';
import { Add, Delete } from '@mui/icons-material';
import { ACCEPTABLE_IMAGE_FORMATS_LIST, MAX_IMAGE_UPLOAD_SIZE } from '@uplift/constants';
import { fileToUrl } from '../molecules';
import { SimpleDropzone, SimpleDropzoneProps } from './SimpleDropzone.component';

export const enum InsuranceDropzoneError {
  FILE_TOO_LARGE = 'file-too-large'
}

export type InsuranceDropzoneProps = {
  name: string;
  altText: string;
  placeholderText: string;
  classes?: ClassNameMap<'preview' | 'empty' | 'error'>;
  clearable?: boolean;
  color?: SimpleDropzoneProps['color'];
  isPartnerInsurance?: boolean;
};


const useStyles = makeStyles<InsuranceDropzoneProps>({ name: 'InsuranceDropzone2' })(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '50px',
    width: '100%',
    cursor: 'pointer',
    padding: '6px',
    boxSizing: 'border-box',
    position: 'relative'
  },
  previewOverlay: {
    position: 'absolute',
    top: 2,
    left: 2,
    height: 'calc(100% - 4px)',
    width: 'calc(100% - 4px)',
    opacity: 0,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.short,
    }),
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      opacity: 1,
    }
  },
  deleteButton: {
    position: 'absolute',
    right: theme.spacing(0.5),
    top: theme.spacing(0.5),
  },
  preview: {
    height: '182px'
  },
  empty: {
    height: '50px'
  },
  error: {

  },
  label: {
    fontSize: '14px',
    textTransform: 'uppercase',
    color: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    whiteSpace: 'nowrap'
  }
}));


class NoAssetFoundError extends Error {}


export const InsuranceDropzone: React.VFC<InsuranceDropzoneProps> = ({
  clearable = true,
  isPartnerInsurance = true,
  ...props
}) => {
  const [ field, meta, helpers ] = useField(props.name);
  const { classes, cx } = useStyles(props, { props });
  const uploadInsurance = useUploadInsurance();
  const [ files, setFiles ] = React.useState<File[]>([]);

  useEffect(() => {
    const fetchFile = async (assetId?: string) => {
      try {
        if (!assetId) {
          throw new NoAssetFoundError();
        }

        const assetUrl = assetId ? await getProtectedAssetUrl(assetId, isPartnerInsurance ? AssetReference.insurance : AssetReference.nonPartnerInsurance) : undefined;

        if (!assetUrl) {
          throw new NoAssetFoundError('Asset not found');
        }
        const assetFile = await downloadAssetFile(assetUrl);

        if (!files.length) {
          setFiles([ assetFile ]);
        }
      } catch (err) {
        if (err instanceof NoAssetFoundError) {
          helpers.setValue(null);
        }
        if (err instanceof Error) {
          console.error(err.message || 'Unable to download file');
        }
      }
    };

    if (!field.value) {
      return;
    }

    if (field.value !== meta.initialValue) {
      return;
    }

    fetchFile(field.value);
  }, [ field.value, meta.initialValue ]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = React.useCallback((files: File[]) => {
    const uploadFile = async (files: File[]) => {
      if (!files.length) {
        return;
      }

      const file = files[0];

      try {
        const assetId = await uploadInsurance(file, file.name);

        helpers.setValue(assetId);
        helpers.setError('');
      } catch (err) {
        console.error(err);
      }
    };

    helpers.setValue(null);
    setFiles(files);
    uploadFile(files);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasError = Boolean(meta.touched && meta.error);
  const fileUrl = React.useMemo(() => {
    if (files?.length) {
      return fileToUrl(files[0]);
    }
    return null;
  }, [ files ]);

  return (
    <UpliftThemeProvider>
      <SimpleDropzone
        color={props.color ?? 'secondary'}
        data-testid={`dropzone-id-${props.name}`}
        error={hasError}
        activeOnHover
        maxSize={MAX_IMAGE_UPLOAD_SIZE}
        accept={ACCEPTABLE_IMAGE_FORMATS_LIST.reduce((prev, curr) => ({ ...prev, [curr]: [] }), {} as Record<string, string[]>)}
        onDropAccepted={(files) => {
          onChange(files);
        }}
      >
        {_state => (
          <Box
            className={cx(classes.root, {
              [classes.preview]: files?.length > 0,
              [classes.empty]: !files?.length,
              [classes.error]: hasError,
            })}
          >
            {fileUrl ? (
              <>
                <Box display="flex" height="100%" width="100%" justifyContent="center" alignItems="center">
                  <img
                    src={fileUrl}
                    alt={props.altText}
                    height="auto"
                    width="auto"
                    style={{
                      borderRadius: '8px',
                      maxWidth: '100%',
                      maxHeight: '100%',
                    }}
                  />
                </Box>
                <Box className={classes.previewOverlay}>
                  <Typography className={classes.label}>
                    <Add fontSize="small" color="inherit" />
                    {props.placeholderText}
                  </Typography>
                  {clearable && (
                    <IconButton
                      className={classes.deleteButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onChange([]);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              </>
            ) : (
              <Typography className={classes.label}>
                <Add fontSize="small" color="inherit" />
                {props.placeholderText}
              </Typography>
            )}
          </Box>
        )}
      </SimpleDropzone>
    </UpliftThemeProvider>
  );
};
